/**
 * Line-items and calculator Zod schemas — shared FE ↔ BE.
 *
 * Per DESIGN.md §5.2 (endpoints 33–38) + ARCHITECTURE.md §3.5 +
 * REQUIREMENTS.md FR-CALC-*, FR-PROJ-004.
 *
 * DB source of truth: apps/api/src/infrastructure/db/schema/projects.ts
 * (the `line_items` table — 30+ explicit columns, no JSONB per C-008).
 *
 * All derived-column DTO fields are nullable:
 *   - Sliding-only columns (interlock, sash_handle, sash_top, sash_bottom, roller)
 *     are null for casement line items.
 *   - Casement-only columns (mullion, fixed_shutter, openable_shutter, lock)
 *     are null for sliding line items.
 *   - CalculatorService sets every inapplicable column to null.
 */
import { z } from 'zod';
import { CALC_MODE_CODES, MATERIAL_CODES } from './catalogue';

/* ─── Shared dimension constants ─────────────────────────────────── */

/** Minimum meaningful window dimension in mm. */
const DIM_MIN_MM = 100;
/** Maximum window/door span in mm (covers doors up to 8m). */
const DIM_MAX_MM = 8000;

/* ─── Create line item ───────────────────────────────────────────── */

/**
 * Body for `POST /api/v1/projects/:projectId/line-items`.
 *
 * The CalculatorService runs immediately on receipt; all derived columns
 * are written to the DB alongside the input fields.
 */
export const CreateLineItemSchema = z.object({
  /** Catalogue series the item belongs to (FK → series.id, per-tenant). */
  seriesId: z.number().int().positive('Series ID must be a positive integer'),

  /**
   * Specific type within the series (FK → types.id, per-tenant).
   * Optional — some series have no configured types; the calculator returns
   * all-null derived columns in that case (same as the ALUK path).
   */
  typeId: z.number().int().positive('Type ID must be a positive integer').optional(),

  /**
   * Optional sash sub-type for multi-sash configurations
   * (e.g. Sliding 2/3/4 centre-opening variants).
   */
  sashTypeId: z.number().int().positive('Sash type ID must be a positive integer').optional(),

  /** Profile material — must match the series' material. */
  material: z.enum(MATERIAL_CODES),

  /**
   * Sliding or Casement — must match the selected type's mode.
   * Optional when typeId is absent (mode cannot be known without a type).
   */
  mode: z.enum(CALC_MODE_CODES).optional(),

  /** New in M-12 (0007): explicit raw user inputs + frame style */
  combinationId: z.number().int().positive().optional(),
  frameStyle: z.enum(['WITH_MULLION', 'SEPARATE_FRAME']).optional(),
  inputUnit: z.enum(['in', 'mm']).default('mm'),
  inputWidthValue: z.number().default(0),
  inputHeightValue: z.number().default(0),
  g1Value: z.number().optional(),
  g2Value: z.number().optional(),

  /** Nominal overall width in mm (input unit; stored as mm). */
  widthMm: z
    .number()
    .int('Width must be a whole number of mm')
    .min(DIM_MIN_MM, `Min width ${DIM_MIN_MM}mm`)
    .max(DIM_MAX_MM, `Max width ${DIM_MAX_MM}mm`),

  /** Nominal overall height in mm. */
  heightMm: z
    .number()
    .int('Height must be a whole number of mm')
    .min(DIM_MIN_MM, `Min height ${DIM_MIN_MM}mm`)
    .max(DIM_MAX_MM, `Max height ${DIM_MAX_MM}mm`),

  /** Number of identical windows/doors at these dimensions. Default 1. */
  qty: z.number().int().min(1, 'Min qty 1').max(999, 'Max qty 999').default(1),

  /**
   * Per-item stock-bar length override (mm).
   * Wins over project-level and tenant-level defaults (FR-OPT-001).
   * Range: 1000–12000 mm.
   */
  stockBarOverrideMm: z
    .number()
    .int('Stock bar length must be a whole number of mm')
    .min(1000, 'Min 1000mm')
    .max(12000, 'Max 12000mm')
    .optional(),

  /** Glass specification code (e.g. "4mm Clear", "6mm Toughened"). Max 32 chars. */
  glassType: z.string().trim().max(32, 'Glass type too long (max 32)').optional(),

  /**
   * Glass panel thickness in mm (e.g. 4.0, 6.0, 8.0, 10.0).
   * Stored as NUMERIC(5,2). Range: 3–25mm.
   */
  glassThicknessMm: z.number().min(3, 'Min 3mm').max(25, 'Max 25mm').optional(),

  /** Installation location or room notes (e.g. "Master bedroom — south wall"). */
  locationNotes: z.string().trim().max(500, 'Location notes too long (max 500)').optional(),

  /** Free-text remarks — maps to the ADR-007 base `remarks` column. */
  remarks: z.string().max(2000, 'Remarks too long (max 2000)').optional(),
});

export type CreateLineItemInput = z.infer<typeof CreateLineItemSchema>;

/* ─── Update line item ───────────────────────────────────────────── */

/**
 * Body for `PATCH /api/v1/line-items/:id`.
 *
 * All fields are optional; at least one must be provided.
 * The CalculatorService re-runs on every PATCH that changes
 * any dimension or catalogue reference.
 */
export const UpdateLineItemSchema = CreateLineItemSchema.partial().refine(
  (data) => Object.keys(data).length > 0,
  { message: 'At least one field must be provided' },
);

export type UpdateLineItemInput = z.infer<typeof UpdateLineItemSchema>;

/* ─── Calculator preview ─────────────────────────────────────────── */

/**
 * Body for `POST /api/v1/calculators/preview`.
 *
 * Identical input surface to CreateLineItem — the calculator runs the
 * same computation but does NOT write to the database.
 * Used by the FE live-preview panel (debounced, 300ms).
 */
export const CalculatorPreviewSchema = CreateLineItemSchema;

export type CalculatorPreviewInput = z.infer<typeof CalculatorPreviewSchema>;

/* ─── List query ─────────────────────────────────────────────────── */

/**
 * Query-string parameters for `GET /api/v1/projects/:projectId/line-items`.
 */
export const ListLineItemsQuerySchema = z.object({
  /** Cursor-based pagination: ID of the last item on the previous page. */
  cursor: z.coerce.number().int().positive().optional(),

  /** Page size. Default 50, max 100. */
  limit: z.coerce.number().int().min(1).max(100).default(50),

  /**
   * Visibility filter. Defaults to `active`.
   * Pass `deleted` for a restore workflow; `all` for admin views.
   */
  status: z.enum(['active', 'deleted', 'all']).default('active').optional(),
});

export type ListLineItemsQuery = z.infer<typeof ListLineItemsQuerySchema>;

/* ─── Derived dimensions DTO ─────────────────────────────────────── */

/**
 * The computed output columns produced by CalculatorService.
 *
 * All fields are nullable because:
 *   - Sliding-only columns are null for casement items.
 *   - Casement-only columns are null for sliding items.
 *   - CalculatorService populates only the columns relevant to the mode.
 */
export const CalculatorResultDtoSchema = z.object({
  /** Outer frame — present for both sliding and casement. */
  outerFrameTopMm: z.number().int().nullable(),
  outerFrameBottomMm: z.number().int().nullable(),
  outerFrameLeftMm: z.number().int().nullable(),
  outerFrameRightMm: z.number().int().nullable(),
  outerFrameQty: z.number().int().nullable(),

  /** Interlock rail — sliding only. */
  interlockMm: z.number().int().nullable(),
  interlockQty: z.number().int().nullable(),

  /** Sash handle bar — sliding only. */
  sashHandleMm: z.number().int().nullable(),
  sashHandleQty: z.number().int().nullable(),

  /** Sash top bar — sliding only. */
  sashTopMm: z.number().int().nullable(),
  sashTopQty: z.number().int().nullable(),

  /** Sash bottom bar — sliding only. */
  sashBottomMm: z.number().int().nullable(),
  sashBottomQty: z.number().int().nullable(),

  /** Mullion — casement only (where applicable). */
  mullionMm: z.number().int().nullable(),
  mullionQty: z.number().int().nullable(),

  /** Fixed shutter — casement only. */
  fixedShutterMm: z.number().int().nullable(),
  fixedShutterQty: z.number().int().nullable(),

  /** Openable shutter — casement only. */
  openableShutterMm: z.number().int().nullable(),
  openableShutterQty: z.number().int().nullable(),

  /** Glass panel dimensions — present for all modes. */
  glassWidthMm: z.number().int().nullable(),
  glassHeightMm: z.number().int().nullable(),
  glassQty: z.number().int().nullable(),

  /** Beading strip — present for all modes. */
  beadingMm: z.number().int().nullable(),
  beadingQty: z.number().int().nullable(),

  /** Rubber/gasket strip — present for all modes. */
  rubberMm: z.number().int().nullable(),
  rubberQty: z.number().int().nullable(),

  /** Roller hardware count — sliding only. */
  rollerQty: z.number().int().nullable(),

  /** Lock hardware count — casement only. */
  lockQty: z.number().int().nullable(),
});

export type CalculatorResultDto = z.infer<typeof CalculatorResultDtoSchema>;

/* ─── Response DTOs ──────────────────────────────────────────────── */

/**
 * Full line-item response shape.
 * Returned by `POST`, `GET`, `PATCH` on line-items and by the preview endpoint.
 */
export const LineItemDtoSchema = z
  .object({
    id: z.number().int().positive(),
    projectId: z.number().int().positive(),
    tenantId: z.number().int().positive(),

    /** Catalogue references. */
    seriesId: z.number().int().positive(),
    /** Null when the series has no configured types. */
    typeId: z.number().int().positive().nullable(),
    sashTypeId: z.number().int().nullable(),
    material: z.enum(MATERIAL_CODES),
    /** Null when typeId is null. */
    mode: z.enum(CALC_MODE_CODES).nullable(),

    /** New in M-12 (0007): explicit raw user inputs + frame style */
    combinationId: z.number().int().positive().nullable(),
    frameStyle: z.enum(['WITH_MULLION', 'SEPARATE_FRAME']).nullable(),
    inputUnit: z.enum(['in', 'mm']),
    inputWidthValue: z.number(),
    inputHeightValue: z.number(),
    g1Value: z.number().nullable(),
    g2Value: z.number().nullable(),

    /** Input dimensions. */
    widthMm: z.number().int(),
    heightMm: z.number().int(),
    qty: z.number().int(),
    stockBarOverrideMm: z.number().int().nullable(),

    /** Glass + location metadata. */
    glassType: z.string().nullable(),
    glassThicknessMm: z.number().nullable(),
    locationNotes: z.string().nullable(),

    /** ADR-007 base columns. */
    isDeleted: z.union([z.literal(0), z.literal(1)]),
    remarks: z.string().nullable(),
    createdBy: z.number().int(),
    createdDate: z.string().datetime(),
    updatedBy: z.number().int(),
    updatedDate: z.string().datetime(),
    deletedBy: z.number().int().nullable(),
    deletedDate: z.string().datetime().nullable(),
  })
  .merge(CalculatorResultDtoSchema);

export type LineItemDto = z.infer<typeof LineItemDtoSchema>;

/** Paginated list of line items — returned by `GET /api/v1/projects/:projectId/line-items`. */
export const PaginatedLineItemsDtoSchema = z.object({
  items: z.array(LineItemDtoSchema),
  nextCursor: z.number().int().nullable(),
  hasMore: z.boolean(),
  limit: z.number().int(),
});

export type PaginatedLineItemsDto = z.infer<typeof PaginatedLineItemsDtoSchema>;

/**
 * Calculator preview response — returned by `POST /api/v1/calculators/preview`.
 *
 * Mirrors the derived columns of a line item but carries no persistence metadata.
 * The FE preview panel renders these values live as the user types.
 */
export const CalculatorPreviewDtoSchema = CalculatorResultDtoSchema;

export type CalculatorPreviewDto = z.infer<typeof CalculatorPreviewDtoSchema>;
